import { Component } from '@theme/component';

/**
 * Custom element that handles delivery day selection and
 * enforces it as a prerequisite for checkout.
 *
 * Pattern: mirrors cart-note.js — uses @theme/component and the theme's
 * native on:change="/updateDeliveryDay" delegation from component.js.
 *
 * The script is registered in snippets/scripts.liquid so it loads on every
 * page (same as all other theme modules), meaning the module is always ready
 * before any cart interaction.
 */
class DeliveryDaySelector extends Component {
  /** @type {number | undefined} */
  #timeout;

  /** @type {MutationObserver | undefined} */
  #accelObserver;

  connectedCallback() {
    super.connectedCallback();

    // Evaluate on initial connection (page load / cart page)
    this.#updateAllButtons();

    // Defer a second evaluation so the select's server-rendered value
    // has been fully applied to the DOM before we read it.
    setTimeout(() => {
      this.#showInitialMessage();
      this.#updateAllButtons();
    }, 150);

    // Watch for Shopify's async-injected accelerated checkout buttons
    // (Shop Pay, G Pay) which appear after our connectedCallback.
    this.#watchAcceleratedButtons();

    // Re-evaluate button states whenever the cart section is morphed.
    // We listen on the nearest section wrapper so the observer is scoped
    // and fires only for changes inside this section.
    const section = this.closest('.shopify-section') ?? document.body;
    this.#accelObserver = new MutationObserver(() => {
      requestAnimationFrame(() => this.#updateAllButtons());
    });
    this.#accelObserver.observe(section, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    if (this.#timeout) clearTimeout(this.#timeout);
    this.#accelObserver?.disconnect();
    super.disconnectedCallback();
  }

  // ─── Handler — called by on:change="/updateDeliveryDay" in the liquid ──────

  /**
   * Handles delivery day selection changes.
   * Arrow function so `this` is always the component instance regardless of
   * how component.js invokes it (it uses callback.call(instance, event)).
   * @param {Event} event
   */
  updateDeliveryDay = async (event) => {
    const select = /** @type {HTMLSelectElement} */ (event.target);
    const selectedDay = select.value;
    const msg = this.querySelector('.delivery-day-selector__message');

    if (this.#timeout) clearTimeout(this.#timeout);

    try {
      // Optimistically update button states before the API call returns
      this.#updateAllButtons();
      this.#showMessage(msg, 'Updating...', false);

      // Preserve any text already in the cart note
      const cartRes = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
      });
      if (!cartRes.ok) throw new Error('Failed to get cart');
      const currentCart = await cartRes.json();

      let note = (currentCart.note || '').replace(/\n?Delivery Day:.*$/gm, '').trim();
      if (selectedDay) {
        note = note ? `${note}\nDelivery Day: ${selectedDay}` : `Delivery Day: ${selectedDay}`;
      }

      // Save the delivery day as a cart attribute and in the note
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          attributes: { 'Delivery Day': selectedDay || '' },
          note,
        }),
      });
      if (!res.ok) throw new Error('Failed to update delivery day');
      const cart = await res.json();

      if (selectedDay) {
        this.#showMessage(
          msg,
          `You'll receive your weekly delivery every <span class="delivery-day-name">${selectedDay}.</span>`,
          false
        );
      } else {
        this.#clearMessage(msg);
      }

      // Keep visible note textarea in sync
      const noteArea = /** @type {HTMLTextAreaElement | null} */ (
        document.querySelector('#cart-note, [name="note"]')
      );
      if (noteArea) noteArea.value = note;

      // Final authoritative button state update after confirmed save
      this.#updateAllButtons();

      // Notify other theme components (cart count etc.)
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    } catch (err) {
      console.error('Error updating delivery day:', err);
      this.#showMessage(msg, 'Failed to update', true);
      this.#timeout = /** @type {number} */ (setTimeout(() => this.#clearMessage(msg), 3000));
    }
  };

  // ─── Private helpers ──────────────────────────────────────────────────────

  /** @returns {HTMLSelectElement | null} */
  #getSelect() {
    return /** @type {HTMLSelectElement | null} */ (
      this.querySelector('#delivery-day-select')
    );
  }

  /** @returns {boolean} */
  #dayIsSelected() {
    const sel = this.#getSelect();
    return !!(sel && sel.value && sel.value !== '');
  }

  #updateAllButtons() {
    this.#updateCheckoutButton();
    this.#updateAcceleratedButtons();
  }

  #updateCheckoutButton() {
    const btn = /** @type {HTMLButtonElement | null} */ (
      document.querySelector('#checkout') ||
      document.querySelector('[name="checkout"]') ||
      document.querySelector('.cart__checkout-button')
    );
    if (!btn) return;

    if (this.#dayIsSelected()) {
      btn.removeAttribute('disabled');
      btn.style.opacity = '1';
      btn.style.cursor = 'pointer';
    } else {
      btn.setAttribute('disabled', 'disabled');
      btn.setAttribute('data-requires-delivery-day', 'true');
      btn.style.opacity = '0.5';
      btn.style.cursor = 'not-allowed';
    }
  }

  #updateAcceleratedButtons() {
    /** @type {NodeListOf<HTMLElement>} */
    const containers = document.querySelectorAll(
      '.additional-checkout-buttons, .cart__additional-checkout-buttons, shopify-accelerated-checkout-cart'
    );
    const selected = this.#dayIsSelected();

    containers.forEach((container) => {
      if (selected) {
        container.style.pointerEvents = '';
        container.style.opacity = '';
        container.title = '';
        container.removeAttribute('data-requires-delivery-day');
        container.querySelector('.delivery-day-required-overlay')?.remove();
        container.style.position = '';
      } else {
        container.setAttribute('data-requires-delivery-day', 'true');
        container.style.opacity = '0.5';
        container.style.pointerEvents = 'none';
        container.title = 'Please select a delivery day first';

        if (!container.querySelector('.delivery-day-required-overlay')) {
          container.style.position = 'relative';
          const overlay = document.createElement('div');
          overlay.className = 'delivery-day-required-overlay';
          overlay.style.cssText =
            'position:absolute;inset:0;z-index:9999;cursor:not-allowed;background:transparent;';
          overlay.setAttribute('aria-hidden', 'true');
          container.appendChild(overlay);
        }
      }
    });
  }

  /**
   * Watches specifically for the accelerated checkout buttons (Shop Pay / G Pay)
   * that Shopify injects asynchronously into .cart__ctas.
   */
  #watchAcceleratedButtons() {
    const root =
      document.querySelector('.cart__ctas') ||
      document.querySelector('.cart-drawer__summary') ||
      document.body;

    const observer = new MutationObserver(() => this.#updateAcceleratedButtons());
    observer.observe(root, { childList: true, subtree: true });

    // Clean up this specific observer on disconnect
    const original = this.disconnectedCallback.bind(this);
    this.disconnectedCallback = () => {
      observer.disconnect();
      original();
    };
  }

  #showInitialMessage() {
    const sel = this.#getSelect();
    const msg = this.querySelector('.delivery-day-selector__message');
    if (sel && sel.value && msg) {
      this.#showMessage(
        msg,
        `You'll receive your weekly delivery every <span class="delivery-day-name">${sel.value}</span>`,
        false
      );
    }
  }

  /**
   * @param {Element | null} el
   * @param {string} html
   * @param {boolean} isError
   */
  #showMessage(el, html, isError) {
    if (!el) return;
    el.innerHTML = html;
    el.classList.toggle('error', isError);
  }

  /** @param {Element | null} el */
  #clearMessage(el) {
    if (!el) return;
    el.textContent = '';
    el.classList.remove('error');
  }
}

if (!customElements.get('delivery-day-selector')) {
  customElements.define('delivery-day-selector', DeliveryDaySelector);
}
